import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import PageWrapper from "../../components/PageWrapper/PageWrapper";
import { resolveOrganizerProfile } from "../../services/adminOrganizerService";
import {
  buildAdminBookingViewPath,
  buildAdminOrganizerProfilePath
} from "../../utils/adminUser";
import {
  formatOrganizerCurrency,
  formatOrganizerDate,
  formatOrganizerTimeRange,
  getOrganizerBookingBadge
} from "../../components/OrganizerDashboard/organizerUtils";

const formatUserName = (user) => {
  if (!user) return "Unknown organiser";
  const parts = [user.first_name, user.last_name].filter(Boolean);
  if (parts.length) return parts.join(" ");
  return user.name || user.email || "Unknown organiser";
};

const AdminOrganizers = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const userIdParam = (searchParams.get("userId") || "").trim();
  const emailParam = (searchParams.get("email") || "").trim();

  const [lookupUserId, setLookupUserId] = useState(userIdParam);
  const [lookupEmail, setLookupEmail] = useState(emailParam);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    setLookupUserId(userIdParam);
    setLookupEmail(emailParam);
  }, [userIdParam, emailParam]);

  useEffect(() => {
    if (!userIdParam && !emailParam) {
      setProfile(null);
      setError("");
      setLoading(false);
      return undefined;
    }

    let cancelled = false;

    (async () => {
      setLoading(true);
      setError("");
      try {
        const result = await resolveOrganizerProfile({
          userId: userIdParam || undefined,
          email: emailParam || undefined
        });
        if (!cancelled) {
          setProfile(result);
          if (!result.user && result.bookings.length === 0) {
            setError("No organiser or bookings found for that lookup.");
          }
        }
      } catch (loadError) {
        if (!cancelled) {
          setProfile(null);
          setError(loadError?.message || "Failed to load organiser profile.");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [userIdParam, emailParam]);

  const viewKey = profile?.viewKey;
  const bookingViewParams = useMemo(
    () => ({
      userId: userIdParam || profile?.user?.id || undefined,
      email: !userIdParam && !profile?.user?.id ? emailParam || profile?.user?.email : undefined
    }),
    [userIdParam, emailParam, profile]
  );

  const handleSearch = (event) => {
    event.preventDefault();
    const nextUserId = lookupUserId.trim();
    const nextEmail = lookupEmail.trim();

    if (!nextUserId && !nextEmail) {
      setError("Enter a user ID or email to look up an organiser.");
      return;
    }

    navigate(buildAdminOrganizerProfilePath({ userId: nextUserId, email: nextEmail }));
  };

  return (
    <PageWrapper useNewFooter useLanding2Header>
      <main className="bg-white">
        <div className="mx-auto w-full max-w-[96rem] px-6 py-10 sm:px-8">
          <div className="mb-8">
            <Link to="/admin" className="text-sm text-[#046242] hover:underline">
              ← Admin tools
            </Link>
            <p className="mt-4 text-[11px] font-medium uppercase tracking-[0.2em] text-gray-500">
              Organisers
            </p>
            <h1 className="font-heading mt-2 text-3xl font-bold text-gray-900">
              Event organiser profiles
            </h1>
            <p className="mt-2 max-w-2xl text-sm text-gray-600">
              Look up an organiser by user ID or booking email, then open their bookings as they
              would see them. Same pattern as venue dashboards with{" "}
              <code className="rounded bg-gray-100 px-1.5 py-0.5 text-xs">?venueId=</code>.
            </p>
          </div>

          <form
            onSubmit={handleSearch}
            className="mb-8 grid gap-4 rounded-xl border border-gray-200 bg-gray-50 p-5 sm:grid-cols-2"
          >
            <label className="block text-sm">
              <span className="mb-1 block font-medium text-gray-700">User ID</span>
              <input
                type="text"
                value={lookupUserId}
                onChange={(event) => setLookupUserId(event.target.value)}
                placeholder="Backend user UUID"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              />
            </label>
            <label className="block text-sm">
              <span className="mb-1 block font-medium text-gray-700">Email</span>
              <input
                type="email"
                value={lookupEmail}
                onChange={(event) => setLookupEmail(event.target.value)}
                placeholder="organiser@example.com"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              />
            </label>
            <div className="sm:col-span-2 flex flex-wrap items-center gap-3">
              <button
                type="submit"
                className="rounded-lg bg-[#046242] px-4 py-2 text-sm font-medium text-white hover:bg-[#035035]"
              >
                Look up organiser
              </button>
              {(userIdParam || emailParam) && (
                <button
                  type="button"
                  className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-white"
                  onClick={() => navigate("/admin/organizers")}
                >
                  Clear
                </button>
              )}
            </div>
          </form>

          {error ? (
            <div className="mb-6 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          ) : null}

          {loading ? (
            <p className="text-sm text-gray-600">Loading organiser profile…</p>
          ) : null}

          {!loading && profile?.user ? (
            <section className="mb-8 rounded-xl border border-gray-200 p-5">
              <h2 className="text-lg font-semibold text-gray-900">{formatUserName(profile.user)}</h2>
              <dl className="mt-3 grid gap-3 text-sm sm:grid-cols-2">
                <div>
                  <dt className="text-gray-500">Email</dt>
                  <dd className="font-medium text-gray-900">{profile.user.email || "—"}</dd>
                </div>
                <div>
                  <dt className="text-gray-500">User ID</dt>
                  <dd className="font-mono text-xs text-gray-900 break-all">
                    {profile.user.id || "Not linked to an account"}
                  </dd>
                </div>
              </dl>
            </section>
          ) : null}

          {!loading && profile?.bookings?.length > 0 ? (
            <section>
              <h2 className="mb-4 text-lg font-semibold text-gray-900">
                Bookings ({profile.bookings.length})
              </h2>
              <ul className="space-y-3">
                {profile.bookings.map((booking) => {
                  const badge = getOrganizerBookingBadge(booking);
                  return (
                    <li
                      key={booking.id}
                      className="rounded-xl border border-gray-200 p-4 transition hover:border-[#046242]/40"
                    >
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                          <p className="font-semibold text-gray-900">
                            {booking.venueName || "Venue booking"}
                          </p>
                          <p className="mt-1 text-sm text-gray-600">
                            {formatOrganizerDate(booking.date)}
                            {formatOrganizerTimeRange(booking.startTime, booking.endTime)
                              ? ` · ${formatOrganizerTimeRange(booking.startTime, booking.endTime)}`
                              : ""}
                          </p>
                          {booking.bookingReference ? (
                            <p className="mt-1 font-mono text-xs text-gray-500">
                              {booking.bookingReference}
                            </p>
                          ) : null}
                        </div>
                        <div className="text-right">
                          <span
                            className="inline-block rounded-full px-2.5 py-1 text-xs font-medium"
                            style={{
                              backgroundColor: badge.backgroundColor,
                              color: badge.color
                            }}
                          >
                            {badge.label}
                          </span>
                          <p className="mt-2 text-sm font-semibold text-gray-900">
                            {formatOrganizerCurrency(booking.totalPrice)}
                          </p>
                        </div>
                      </div>
                      <div className="mt-4">
                        <Link
                          to={buildAdminBookingViewPath(booking.id, {
                            userId: bookingViewParams.userId || viewKey,
                            email: bookingViewParams.email
                          })}
                          className="text-sm font-medium text-[#046242] hover:underline"
                        >
                          View booking as organiser →
                        </Link>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </section>
          ) : null}

          {!loading && (userIdParam || emailParam) && profile && profile.bookings.length === 0 && !error ? (
            <p className="text-sm text-gray-600">No bookings found for this organiser.</p>
          ) : null}
        </div>
      </main>
    </PageWrapper>
  );
};

export default AdminOrganizers;
